import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cropApi } from '@/services/cropApi';

export default function LoginPage() {
  const [deviceId, setDeviceId] = useState('GF-CROP-001');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = deviceId.trim();
    if (!trimmed) {
      setError('Device ID를 입력해주세요.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await cropApi.login(trimmed);
      if (res.result === 'success') {
        localStorage.setItem('device_id', trimmed);
        navigate('/dashboard');
      } else {
        setError(res.message || '등록되지 않은 Device ID입니다.');
      }
    } catch {
      setError('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{
        background: 'linear-gradient(135deg, #0f2d1a 0%, #1B4F72 60%, #145a32 100%)',
      }}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="absolute text-white text-8xl select-none"
              style={{
                top: `${10 + i * 12}%`,
                left: `${(i * 13) % 90}%`,
                transform: `rotate(${i * 15}deg)`,
                fontSize: '6rem',
              }}
            >
              🌾
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20">
              <img
                src="https://readdy.ai/api/search-image?query=rice%20paddy%20field%20aerial%20view%20green%20lush%20healthy%20crop%20top%20down%20shot%20bright%20daylight%20clear%20sky%20simple%20background&width=80&height=80&seq=logo1&orientation=squarish"
                alt="Logo"
                className="w-16 h-16 object-cover object-top"
              />
            </div>
          </div>
          <h1
            className="text-3xl font-bold text-white tracking-tight"
            style={{ fontFamily: '\'Malgun Gothic\', \'맑은 고딕\', sans-serif' }}
          >
            벼 작황 모니터링
          </h1>
          <p className="text-green-300 mt-1 text-sm">Rice Crop Monitoring System</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Card Header */}
          <div className="px-8 py-5" style={{ background: 'linear-gradient(90deg, #1B4F72, #27ae60)' }}>
            <div className="flex items-center gap-2 text-white">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-login-box-line text-lg"></i>
              </div>
              <span className="font-semibold text-base">장치 조회</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-8">
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Device ID
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-cpu-line text-lg"></i>
                </div>
                <input
                  type="text"
                  value={deviceId}
                  onChange={e => { setDeviceId(e.target.value); setError(''); }}
                  placeholder="예: GF-CROP-001"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500 transition-colors"
                  style={{ fontFamily: '\'Malgun Gothic\', \'맑은 고딕\', sans-serif' }}
                  disabled={loading}
                />
              </div>
              {error && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-400">
                모니터링할 장치의 ID를 입력하세요.
              </p>
            </div>

            {/* Quick Select */}
            <div className="mb-6">
              <p className="text-xs text-gray-500 mb-2 font-medium">빠른 선택</p>
              <div className="flex flex-wrap gap-2">
                {['GF-CROP-001', 'GF-CROP-002', 'GF-CROP-003'].map(id => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { setDeviceId(id); setError(''); }}
                    disabled={loading}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap"
                    style={
                      deviceId === id
                        ? { borderColor: '#27ae60', backgroundColor: '#27ae60', color: '#fff' }
                        : { borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#374151' }
                    }
                  >
                    {id}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl text-white font-bold text-base cursor-pointer whitespace-nowrap transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg, #1B4F72, #27ae60)' }}
            >
              <span className="flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin"></i>
                    </div>
                    확인 중...
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-search-line"></i>
                    </div>
                    조회하기
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Footer */}
          <div className="px-8 pb-6 text-center">
            <p className="text-xs text-gray-400">
              © 2026 Rice Crop Monitoring System. All rights reserved.
            </p>
          </div>
        </div>

        {/* Info cards */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            { icon: 'ri-image-2-line', label: 'RGB / NIR / ToF', sub: '4채널 이미지' },
            { icon: 'ri-leaf-line', label: 'NDVI 분석', sub: '식생 지수 추이' },
            { icon: 'ri-bar-chart-line', label: '히스토리', sub: '촬영 이력 조회' },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/20"
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                <i className={`${item.icon} text-green-300 text-xl`}></i>
              </div>
              <p className="text-white text-xs font-semibold">{item.label}</p>
              <p className="text-green-300 text-xs mt-0.5">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
